import {HttpClient, HttpEventType} from '@angular/common/http';
import {Injectable} from '@angular/core';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { Platform } from '@ionic/angular';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';

import {UserContent} from 'app/profile/model/model';
import {environment} from 'environment';
import {AuthRequest} from 'shared/auth-request/auth-request';
import { ContentFlow } from '../model/model';

const HTTP_OK_CODE: number = 200;

@Injectable()
export class ContentRepository {

  constructor(
    private http: HttpClient,
    private authRequest: AuthRequest,
    private platform: Platform,
    private nativeHttp: HTTP,
    private fileOpener: FileOpener,
    private file: File,
  ) { }

  public getContents(type:string): Observable<Array<UserContent>> {
    return this.authRequest.handleRequest(
      this.http.get<Array<UserContent>>(environment.content.contentRoute + '?type='+ type, {withCredentials: true}));
  }

  public getContent(userContentId: string): Observable<UserContent> {
    return this.authRequest.handleRequest(
      this.http.get<UserContent>(`${environment.content.contentRoute}/${userContentId}`, {withCredentials: true}));
  }

  public getContentFlow(userContentId: string): Observable<UserContent> {
    return this.authRequest.handleRequest(
      this.http.get<UserContent>(`${environment.content.contentRoute}/${userContentId}?type=flow`, {withCredentials: true}));
  }

  public calculateNewContents(): Observable<number> {
    return this.authRequest.handleRequest(
      this.http.get<number>(environment.content.calculateNewContentsRoute, {withCredentials: true}));
  }

  public start(userContentId: string): Observable<boolean> {
    return this.authRequest.handleRequest(
      this.http.get(`${environment.content.startRoute}/${userContentId}`, {observe: 'response', responseType: 'text', withCredentials: true}))
      .pipe(map((response) => response.status === HTTP_OK_CODE));
  }

  public pause(userContentId: string): Observable<boolean> {
    return this.authRequest.handleRequest(
      this.http.get(`${environment.content.pauseRoute}/${userContentId}`, {observe: 'response', responseType: 'text', withCredentials: true}))
      .pipe(map((response) => response.status === HTTP_OK_CODE));
  }

  public resume(userContentId: string): Observable<boolean> {
    return this.authRequest.handleRequest(
      this.http.get(`${environment.content.resumeRoute}/${userContentId}`, {observe: 'response', responseType: 'text', withCredentials: true}))
      .pipe(map((response) => response.status === HTTP_OK_CODE));
  }

  public restart(userContentId: string): Observable<boolean> {
    return this.authRequest.handleRequest(
      this.http.get(`${environment.content.restartRoute}/${userContentId}`, {observe: 'response', responseType: 'text', withCredentials: true}))
      .pipe(map((response) => response.status === HTTP_OK_CODE));
  }

  public purchase(userContentId: string, productId: string): Observable<boolean> {
    return this.authRequest.handleRequest(
      this.http.post(environment.content.contentRoute, {userContentId: userContentId, productId: productId}, {observe: 'response', responseType: 'text', withCredentials: true})
        .pipe(map((response) => response.status === HTTP_OK_CODE)));
  }

  public getPdf(userContentId: string, fileName: string, type: string): Promise<string> {
    if (this.platform.is('capacitor')) {
      if ( this.platform.is('android')) {
        const fullFileName: string = this.file.dataDirectory + encodeURI(fileName) + '.pdf';
        return this.nativeHttp.downloadFile(`${environment.content.getPdfRoute}/${userContentId}?type=${type}`, {}, {}, fullFileName)
          .then((response) => {
            // prints 200
            return this.fileOpener.open(fullFileName, 'application/pdf')
            .then(() => {
              return 'ANDROID: OPENED';
            })
            .catch((error) => {
              return 'ANDROID: DOWNLOADED';
            });
          }).catch((err) => {
            // prints 403
            console.log('error block ... ', err.status);
            // prints Permission denied
            console.log('error block ... ', err.error);
            return 'ANDROID: FAILED';
        });
      } else if ( this.platform.is('ios')) {
        const fullFileName: string = this.file.documentsDirectory + 'Download/' + fileName + '.pdf';
        return this.nativeHttp.downloadFile(`${environment.content.getPdfRoute}/${userContentId}?type=${type}`, {}, {}, fullFileName)
          .then((response) => {
            // prints 200
            return this.fileOpener.open(fullFileName, 'application/pdf')
            .then(() => {
              return 'IOS: OPENED';
            })
            .catch((error) => {
              return 'IOS: DOWNLOADED';
            });
         }).catch((err) => {
            // prints 403
            console.log('error block ... ', err.status);
            // prints Permission denied
            console.log('error block ... ', err.error);
            return 'IOS: FAILED';
        });
      }
    } else {
      window.open(`${environment.content.getPdfRoute}/${userContentId}?type=${type}`, '_blank');
    }
    return Promise.resolve('BROWSER: OK');
  }

}
